import {
  Box,
  VStack,
  chakra,
  keyframes,
  useColorModeValue,
  Stack,
  Card,
  CardBody,
  Heading,
  Text,
  Divider,
  Badge,
  Flex,
} from '@chakra-ui/react';
import Header from '../../../../../components/Header/Header';
import CardHeaderMobile from '../../../../../components/Mobile/header';
import LabelWithValueMobilePage from '../../../../../components/Mobile/labelWithValue';
import { IGetRegistration } from '../../../../../services/hooks/Registration/useGetRegistration';
import HeaderSectionMobilePage from '../../../../../components/Mobile/headerSection';
import { IPreliminaryResult } from '../../../../../services/hooks/CandidateArea/useGetPreliminaryResult';
import ClassificationBoxMobilePage from './ClassificationBox';
import { TbMessage, TbMessageCheck, TbMessageReport } from 'react-icons/tb';

const floatIn = keyframes`
  from { opacity: 0; transform: translateY(18px) scale(0.995); }
  to   { opacity: 1; transform: translateY(0px) scale(1); }
`;
const AnimatedBox = chakra(Box, {
  baseStyle: {
    animation: `${floatIn} 420ms cubic-bezier(.2,.9,.3,1) both`,
  },
});

interface IOwnProps {
  registration?: IGetRegistration | null;
  result?: IPreliminaryResult;
}

const ResultLegacyMobilePage: React.FC<IOwnProps> = ({ registration, result }) => {
  const borderColor = useColorModeValue('gray.100', 'gray.700');

  const verifyResultPreliminary = !!(
    result?.classifications?.preliminary?.extensive_competition?.status ||
    result?.classifications?.preliminary?.black_or_brown?.status ||
    result?.classifications?.preliminary?.pcd?.status ||
    result?.classifications?.preliminary?.general?.status
  );

  const verifyResultFinal = !!(
    result?.classifications?.final?.extensive_competition?.status ||
    result?.classifications?.final?.black_or_brown?.status ||
    result?.classifications?.final?.pcd?.status ||
    result?.classifications?.final?.general?.status
  );

  return (
    <Stack h="100dvh" w="100dvw">
      <Stack height="100%" w="100%" top={0} gap={0}>
        <Header showGoBack />
        <VStack mt={0} mx={3} flex={1} overflowY="auto" py={2}>
          <AnimatedBox
            borderRadius="lg"
            boxShadow="lg"
            mx={3}
            p={{ base: 5, md: 10 }}
            borderWidth={'1px'}
            borderColor={borderColor}
            bg="white"
            flex={1}
            display="flex"
            w="full"
          >
            <VStack boxSizing="border-box" flex={1} spacing={2} align="stretch">
              <CardHeaderMobile
                title="Resultado"
                subTitle="Proteja sua conta com uma senha forte — nunca compartilhe com ninguém."
              />
              <Box mt={4} w="full">
                <HeaderSectionMobilePage>
                  {registration?.selective_process_vacancy?.selective_process?.name}
                </HeaderSectionMobilePage>
                <Card borderRadius="md" shadow="md" mb={4} bg="white">
                  <CardBody>
                    <VStack spacing={0} align="start" fontSize="sm">
                      <Text fontWeight="bold" fontSize="md" color="gray.800">
                        {registration?.candidate.user?.full_name || '--'}
                      </Text>
                      {registration?.candidate?.social_name && (
                        <Text color="gray.500" fontSize="0.8rem">
                          Nome social: {registration.candidate.social_name || '--'}
                        </Text>
                      )}

                      <Divider my={2} />

                      <Stack>
                        <LabelWithValueMobilePage label="Inscrição" value={registration?.registration_code || '--'} />
                        <LabelWithValueMobilePage
                          label={registration?.candidate.is_foreign ? 'Passaporte' : 'CPF'}
                          value={
                            registration?.candidate.is_foreign
                              ? registration?.candidate?.passport_number
                              : registration?.candidate?.cpf || '--'
                          }
                        />
                        <LabelWithValueMobilePage
                          label={
                            registration?.selective_process_vacancy?.cultural_house
                              ? registration?.selective_process_vacancy.class_level
                                ? 'Turma: '
                                : 'Turma - Primeira opção: '
                              : 'Vaga: '
                          }
                          value={registration?.selective_process_vacancy?.name || ''}
                        />
                        {registration?.second_vacancy?.name && (
                          <LabelWithValueMobilePage
                            label="Turma - Segunda opção"
                            value={registration?.second_vacancy.name || '--'}
                          />
                        )}
                      </Stack>
                    </VStack>
                  </CardBody>
                </Card>

                <Card borderRadius="md" shadow="md" mb={6} bg="white">
                  <CardBody>
                    <Flex gap={1} color="blue.600">
                      <TbMessage />
                      <Heading size="sm" mb={4}>
                        Nota das provas
                      </Heading>
                    </Flex>

                    <VStack spacing={3} align="stretch">
                      {result?.notes.map((r, i) => (
                        <Stack
                          key={i}
                          direction="row"
                          justify="space-between"
                          align="center"
                          p={2}
                          borderRadius="lg"
                          bg="gray.50"
                          _hover={{ bg: 'gray.100' }}
                        >
                          <Text fontSize="sm">{r.name}</Text>
                          <Stack>
                            <Badge textAlign="center" colorScheme="green" px={3} py={1} borderRadius="md" fontSize="sm">
                              {r.value}
                              <Text mt={-0.5} fontSize="xx-small">
                                pontos
                              </Text>
                            </Badge>
                          </Stack>
                        </Stack>
                      ))}
                    </VStack>
                  </CardBody>
                </Card>
                {verifyResultPreliminary && (
                  <Card borderRadius="md" shadow="md" mb={6} bg="white">
                    <CardBody>
                      <Flex gap={1} color="yellow.600">
                        <TbMessageReport />
                        <Heading size="sm" mb={4}>
                          Resultado preliminar
                        </Heading>
                      </Flex>

                      <VStack spacing={3} align="stretch">
                        {result?.classifications?.preliminary?.extensive_competition?.status && (
                          <ClassificationBoxMobilePage
                            title={result?.is_cultural_house ? '' : 'Ampla concorrência'}
                            classification={
                              result?.classifications?.preliminary?.extensive_competition?.classification || ''
                            }
                            status={result?.classifications?.preliminary?.extensive_competition?.status || ''}
                          />
                        )}

                        {result?.classifications?.preliminary?.black_or_brown?.status && (
                          <ClassificationBoxMobilePage
                            title={result?.is_cultural_house ? '' : 'Pretos e pardos'}
                            classification={result?.classifications?.preliminary?.black_or_brown?.classification || ''}
                            status={result?.classifications?.preliminary?.black_or_brown.status || ''}
                          />
                        )}

                        {result?.classifications?.preliminary?.pcd?.status && (
                          <ClassificationBoxMobilePage
                            title={result?.is_cultural_house ? '' : 'Pessoa com deficiência'}
                            classification={result?.classifications?.preliminary?.pcd?.classification || ''}
                            status={result?.classifications?.preliminary?.pcd?.status || ''}
                          />
                        )}

                        {result?.classifications?.preliminary?.general?.status && (
                          <ClassificationBoxMobilePage
                            title={result?.is_cultural_house ? '' : 'Geral'}
                            classification={result?.classifications?.preliminary?.general?.classification || ''}
                            status={result?.classifications?.preliminary?.general?.status || ''}
                          />
                        )}
                      </VStack>
                    </CardBody>
                  </Card>
                )}
                {verifyResultFinal && (
                  <Card borderRadius="md" shadow="md" mb={6} bg="white">
                    <CardBody>
                      <Flex gap={1} color="green.600">
                        <TbMessageCheck />
                        <Heading size="sm" mb={4}>
                          Resultado final
                        </Heading>
                      </Flex>
                      <VStack spacing={3} align="stretch">
                        {result?.classifications?.preliminary?.extensive_competition?.status && (
                          <ClassificationBoxMobilePage
                            isFinalResult
                            title={result?.is_cultural_house ? '' : 'Ampla concorrência'}
                            classification={
                              result?.classifications?.preliminary?.extensive_competition?.classification || ''
                            }
                            status={result?.classifications?.preliminary?.extensive_competition?.status || ''}
                          />
                        )}

                        {result?.classifications?.preliminary?.black_or_brown?.status && (
                          <ClassificationBoxMobilePage
                            isFinalResult
                            title={result?.is_cultural_house ? '' : 'Pretos e pardos'}
                            classification={result?.classifications?.preliminary?.black_or_brown?.classification || ''}
                            status={result?.classifications?.preliminary?.black_or_brown.status || ''}
                          />
                        )}

                        {result?.classifications?.preliminary?.pcd?.status && (
                          <ClassificationBoxMobilePage
                            isFinalResult
                            title={result?.is_cultural_house ? '' : 'Pessoa com deficiência'}
                            classification={result?.classifications?.preliminary?.pcd?.classification || ''}
                            status={result?.classifications?.preliminary?.pcd?.status || ''}
                          />
                        )}

                        {result?.classifications?.preliminary?.general?.status && (
                          <ClassificationBoxMobilePage
                            isFinalResult
                            title={result?.is_cultural_house ? '' : 'Geral'}
                            classification={result?.classifications?.preliminary?.general?.classification || ''}
                            status={result?.classifications?.preliminary?.general?.status || ''}
                          />
                        )}
                      </VStack>
                    </CardBody>
                  </Card>
                )}
              </Box>
            </VStack>
          </AnimatedBox>
        </VStack>
      </Stack>
    </Stack>
  );
};

export default ResultLegacyMobilePage;
